import type { CasinoGameLite } from '@/types/casino';

// The games (and their order) the Home page's Hot and Recommended rails
// show, taken from the supplied HOT_GAME list. Recommended continues where
// Hot leaves off (only the first few are used; clip cards lead each rail). Artwork is stored locally (public/images/game-art): the
// supplied CDN links served 2-3MB animated PNGs that made the page crawl.
interface CuratedGame {
  name: string;
  provider: string;
  image: string;
}

const HOT: CuratedGame[] = [
  { name: 'Aviator', provider: 'Spribe', image: '/images/game-art/aviator.webp' },
  { name: 'Fortune Gems', provider: 'Jili', image: '/images/game-art/fortune-gems.webp' },
  { name: 'Fortune Gems 3', provider: 'Jili', image: '/images/game-art/fortune-gems-3.webp' },
  { name: 'Super Ace', provider: 'Jili', image: '/images/game-art/super-ace.webp' },
  { name: 'Money Coming', provider: 'Jili', image: '/images/game-art/money-coming.webp' },
  { name: 'Wild Bounty Showdown', provider: 'PG Soft', image: '/images/game-art/wild-bounty-showdown.webp' },
  { name: 'Ocean King Jackpot', provider: 'Jili', image: '/images/game-art/ocean-king-jackpot.webp' },
  { name: 'Fortune Gems 2', provider: 'Jili', image: '/images/game-art/fortune-gems-2.webp' },
  { name: 'Jackpot Fishing', provider: 'Jili', image: '/images/game-art/jackpot-fishing.webp' },
  { name: 'Fortune King Jackpot', provider: 'Jili', image: '/images/game-art/fortune-king-jackpot.webp' },
  { name: 'Pappu', provider: 'Jili', image: '/images/game-art/pappu.webp' },
  { name: 'Treasures of Aztec', provider: 'PG Soft', image: '/images/game-art/treasures-of-aztec.webp' },
];

const RECOMMENDED: CuratedGame[] = [
  { name: 'Mines', provider: 'Jili', image: '/images/game-art/mines.webp' },
  { name: 'Ganesha Fortune', provider: 'PG Soft', image: '/images/game-art/ganesha-fortune.webp' },
  // Real catalog has it as "MineIsland" (no space/apostrophe) — matched on that.
  { name: 'MineIsland', provider: 'Smartsoft', image: '/images/game-art/mine-s-island.webp' },
  { name: 'Super Ace Deluxe', provider: 'Jili', image: '/images/game-art/super-ace-deluxe.webp' },
  // Real catalog has it as "Money Coming Expand Bets" (not "Expanded").
  { name: 'Money Coming Expand Bets', provider: 'Jili', image: '/images/game-art/money-coming-expanded-bets.webp' },
  { name: 'Golden Empire', provider: 'Jili', image: '/images/game-art/golden-empire.webp' },
  { name: 'Fortune Garuda 500', provider: 'Jili', image: '/images/game-art/fortune-garuda-500.webp' },
  { name: 'JetX', provider: 'Smartsoft', image: '/images/game-art/jetx.webp' },
  { name: 'Ice Fishing', provider: 'Evolution', image: '/images/game-art/ice-fishing.webp' },
  { name: 'Shade Dragons Fishing', provider: 'JDB', image: '/images/game-art/shade-dragons-fishing.webp' },
  { name: 'Pinata Wins', provider: 'PG Soft', image: '/images/game-art/pinata-wins.webp' },
  { name: 'Cai Shen Fishing', provider: 'JDB', image: '/images/game-art/cai-shen-fishing.webp' },
];

// Titles that have a preview clip (see gamePreviewVideos.ts) — shown first so
// the animation is what people see. They carry no still image: the card
// shows the clip's own first frame.
const HOT_VIDEO: CuratedGame[] = [
  { name: 'Andar Bahar', provider: 'Ezugi', image: '' },
  { name: 'Aviatrix', provider: 'Spribe', image: '' },
  { name: 'Color Game', provider: 'JiLi Gaming', image: '' },
  { name: 'Crazy 777', provider: 'JiLi Gaming', image: '' },
  { name: 'Fortune Gems 500', provider: 'JiLi Gaming', image: '' },
  { name: 'Funky Time', provider: 'Evolution', image: '' },
  { name: 'Mines', provider: 'Spribe', image: '' },
  { name: 'Plinko', provider: 'Spribe', image: '' },
  { name: 'Sweet Bonanza', provider: 'Pragmatic Play', image: '' },
  { name: 'Teen Patti 20-20', provider: 'Ezugi', image: '' },
];

const RECOMMENDED_VIDEO: CuratedGame[] = [
  // No "Card Matka" exists anywhere in the catalog (checked platform-wide
  // and MAC88's full 187-game list) — closest real substitute is this one.
  { name: 'Worli Matka', provider: 'MAC88', image: '' },
  { name: 'Coin Toss', provider: 'Spribe', image: '' },
  { name: 'Crazy Time', provider: 'Evolution', image: '' },
  { name: 'Dragon Tiger', provider: 'Ezugi', image: '' },
  { name: 'Fortune Gems 500', provider: 'JiLi Gaming', image: '' },
  { name: 'Sic Bo', provider: 'Ezugi', image: '' },
  { name: 'Speed Baccarat', provider: 'Evolution', image: '' },
  { name: 'Super Ace', provider: 'JiLi Gaming', image: '' },
];

const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Turns a curated list into rail cards. A game that exists in the real
 * catalog (matched by name) is used as-is, so it launches normally. Any that
 * don't get a synthetic card (negative id — Home shows "coming soon" on tap
 * instead of trying to open a session) using the supplied artwork.
 */
function build(
  list: CuratedGame[],
  catalog: CasinoGameLite[],
  idOffset: number
): CasinoGameLite[] {
  const byName = new Map<string, CasinoGameLite>();
  for (const game of catalog) {
    const key = normalize(game.name);
    if (!byName.has(key)) byName.set(key, game);
  }

  return list.map(
    (entry, index) =>
      byName.get(normalize(entry.name)) ?? {
        id: idOffset - index,
        name: entry.name,
        gameImage: entry.image,
        gameProviderName: entry.provider,
      }
  );
}

// Clip cards first, then the curated list — 16 per rail (an even count, which
// the two-row loop needs).
export const buildHotGames = (catalog: CasinoGameLite[]) => [
  ...build(HOT_VIDEO, catalog, -201),
  ...build(HOT.slice(0, 6), catalog, -1),
];
export const buildRecommendedGames = (catalog: CasinoGameLite[]) => [
  ...build(RECOMMENDED_VIDEO, catalog, -301),
  ...build(RECOMMENDED.slice(0, 8), catalog, -101),
];

// Every title either rail can possibly need a real match for — passed as
// `names` to /casino/games so one small, targeted request finds whichever of
// them actually exist as real (launchable) games, instead of a generic
// catalog page that may not happen to contain any of them.
export const HOME_CURATED_GAME_NAMES = [
  ...new Set(
    [...HOT_VIDEO, ...HOT.slice(0, 6), ...RECOMMENDED_VIDEO, ...RECOMMENDED.slice(0, 8)].map(
      (game) => game.name
    )
  ),
].join(',');
